import { scoreHex } from '@/lib/readiness-colors'
import { cn } from '@/lib/utils'

/**
 * Small donut showing a completion percentage in its centre.
 *
 * `score` colours the arc by readiness (red → green) for posture numbers;
 * `ink` is neutral progress (setup steps), where a low number is a starting
 * point, not a warning.
 * `progress` is blue until complete, then green.
 */
export function RadialPercent({
  pct,
  size = 44,
  stroke = 5,
  tone = 'score',
  showPercent = false,
  className,
}: {
  pct: number
  size?: number
  stroke?: number
  tone?: 'score' | 'ink' | 'progress'
  showPercent?: boolean
  className?: string
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(pct)))
  const r = (size - stroke) / 2
  const circumference = 2 * Math.PI * r
  const offset = circumference * (1 - clamped / 100)
  return (
    <div
      className={cn('relative shrink-0', className)}
      style={{ width: size, height: size }}
      role='img'
      aria-label={`${clamped}% complete`}
    >
      <svg width={size} height={size} className='-rotate-90'>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill='none'
          className='stroke-muted'
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill='none'
          className={cn(
            'transition-[stroke-dashoffset] duration-500',
            tone === 'ink' && 'stroke-primary',
            tone === 'progress' &&
              (clamped === 100 ? 'stroke-status-green' : 'stroke-cobalt-strong')
          )}
          stroke={tone === 'score' ? scoreHex(clamped) : undefined}
          strokeWidth={stroke}
          strokeLinecap='round'
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className='absolute inset-0 flex items-center justify-center text-[11px] font-semibold tabular-nums text-text-secondary'>
        {clamped}
        {showPercent && '%'}
      </span>
    </div>
  )
}
